// pages/AllLoans.tsx
import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  PlusIcon,
  BanknotesIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  ArrowPathIcon,
  CreditCardIcon,
  TrashIcon,
  ChartBarIcon,
  MagnifyingGlassIcon,
  UsersIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '@/hooks/useAuth'
import { loanService, LoanType, LoanApplication, Loan } from '@/services/loanService'
import LoanApplicationModal from '@/components/LoanApplicationModal'
import LoanApplicationApprovalModal from '@/components/LoanApplicationApprovalModal'
import LoanPaymentModal from '@/components/LoanPaymentModal'
// import LoanTypeModal from '@/components/LoanTypeModal'
import toast from 'react-hot-toast'

const AllLoans: React.FC = () => {
  const { user } = useAuth()
  const [showLoanApplicationModal, setShowLoanApplicationModal] = useState(false)
  const [showApprovalModal, setShowApprovalModal] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [_showLoanTypeModal, _setShowLoanTypeModal] = useState(false)
  const [selectedApplication, setSelectedApplication] = useState<LoanApplication | null>(null)
  const [selectedLoan, setSelectedLoan] = useState<Loan | null>(null)
  const [_selectedLoanType, _setSelectedLoanType] = useState<LoanType | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  // Fetch loan types
  const { data: _loanTypesData, isLoading: _loadingTypes, refetch: refetchTypes } = useQuery({
    queryKey: ['loanTypes'],
    queryFn: () => loanService.getLoanTypes(true),
    staleTime: 5 * 60 * 1000,
  })

  // Fetch loan applications
  const { data: _applicationsData, isLoading: _loadingApplications, refetch: refetchApplications } = useQuery({
    queryKey: ['loanApplications'],
    queryFn: () => loanService.getLoanApplications(),
    staleTime: 5 * 60 * 1000,
  })

  // Fetch active loans
  const { data: loansData, isLoading: loadingLoans, refetch: refetchLoans } = useQuery({
    queryKey: ['loans'],
    queryFn: () => loanService.getLoans(),
    staleTime: 5 * 60 * 1000,
  })

//   const loanTypes: LoanType[] = loanTypesData?.data?.loanTypes || []
//   const loanApplications: LoanApplication[] = applicationsData?.data?.loanApplications || []
  const activeLoans: Loan[] = loansData?.data?.loans || []

  // Filter loans based on search and status
  const filteredLoans = activeLoans.filter(loan => {
    const matchesSearch =
      loan.employee.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      loan.employee.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      loan.loanType.name.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus = statusFilter === 'all' || loan.status === statusFilter

    return matchesSearch && matchesStatus
  })

  const handleRefresh = async () => {
    try {
      await Promise.all([
        refetchTypes(),
        refetchApplications(),
        refetchLoans()
      ])
      toast.success('Data refreshed successfully')
    } catch (error) {
      toast.error('Failed to refresh data')
    }
  }

  const handleDeleteLoan = async (loan: Loan) => {
    if (!window.confirm(`Are you sure you want to delete the ${loan.loanType.name} loan for ${loan.employee.firstName} ${loan.employee.lastName}? This action cannot be undone.`)) {
      return
    }

    try {
      await loanService.deleteLoan(loan.id)
      toast.success('Loan deleted successfully')
      refetchLoans()
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || 'Failed to delete loan'
      toast.error(errorMessage)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
      case 'manager_approved':
      case 'hr_approved':
      case 'finance_approved':
        return 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400'
      case 'approved':
      case 'disbursed':
      case 'active':
        return 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
      case 'rejected':
        return 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
      case 'completed':
        return 'bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-400'
      default:
        return 'bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-400'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  const getDashboardStats = () => {
    const totalLoans = activeLoans.length
    const activeLoansCount = activeLoans.filter(loan => loan.status === 'active').length
    const completedLoans = activeLoans.filter(loan => loan.status === 'completed').length
    const totalOutstanding = activeLoans
      .filter(loan => loan.status === 'active')
      .reduce((sum, loan) => sum + Number(loan.outstandingBalance), 0)

    return { totalLoans, activeLoansCount, completedLoans, totalOutstanding }
  }

  const stats = getDashboardStats()

  const StatCard = ({ title, value, icon: Icon, iconColorClass, iconBgClass }: {
    title: string;
    value: string | number;
    icon: React.ElementType;
    iconColorClass: string;
    iconBgClass: string;
  }) => (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card hover:shadow-md transition-all duration-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">{title}</p>
          <p className="text-base font-bold text-gray-900 dark:text-white">{value}</p>
        </div>
        <div className={`h-8 w-8 rounded flex items-center justify-center ${iconBgClass}`}>
          <Icon className={`h-4 w-4 ${iconColorClass}`} />
        </div>
      </div>
    </div>
  )

  const canCreateApplication = user?.role === 'employee'
  const canRecordPayment = user && ['admin', 'hr_manager', 'finance_manager'].includes(user.role)
  const canDeleteLoan = user && ['admin', 'hr_manager', 'finance_manager'].includes(user.role)

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3 mb-4 lg:mb-0">
          <div className="h-10 w-10 rounded-lg bg-indigo-600 flex items-center justify-center">
            <BanknotesIcon className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">All Loans Management</h1>
            <p className="text-gray-500 dark:text-gray-400 text-xs">
              Comprehensive overview and management of all employee loans
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Link
            to="/loans/applications"
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium inline-flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
          >
            <DocumentTextIcon className="h-3 w-3" />
            View Applications
          </Link>
          <button
            onClick={handleRefresh}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium inline-flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
          >
            <ArrowPathIcon className="h-3 w-3" />
            Refresh
          </button>
          {canCreateApplication && (
            <button
              onClick={() => setShowLoanApplicationModal(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium inline-flex items-center gap-2 px-3 py-2 transition-all duration-200 hover:shadow-md"
            >
              <PlusIcon className="h-3 w-3" />
              New Application
            </button>
          )}
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search by employee or loan type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Loans"
          value={stats.totalLoans}
          icon={DocumentTextIcon}
          iconColorClass="text-indigo-600 dark:text-indigo-400"
          iconBgClass="bg-indigo-50 dark:bg-indigo-900/20"
        />
        <StatCard
          title="Active Loans"
          value={stats.activeLoansCount}
          icon={UsersIcon}
          iconColorClass="text-emerald-600 dark:text-emerald-400"
          iconBgClass="bg-emerald-50 dark:bg-emerald-900/20"
        />
        <StatCard
          title="Completed Loans"
          value={stats.completedLoans}
          icon={ChartBarIcon}
          iconColorClass="text-indigo-600 dark:text-indigo-400"
          iconBgClass="bg-indigo-50 dark:bg-indigo-900/20"
        />
        <StatCard
          title="Total Outstanding"
          value={formatCurrency(stats.totalOutstanding)}
          icon={CurrencyDollarIcon}
          iconColorClass="text-amber-600 dark:text-amber-400"
          iconBgClass="bg-amber-50 dark:bg-amber-900/20"
        />
      </div>

      {/* Loans Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <BanknotesIcon className="h-4 w-4" />
            All Employee Loans
          </h2>
        </div>

        {loadingLoans ? (
          <div className="flex justify-center items-center py-8">
            <div className="h-8 w-8 rounded-full border-[3px] border-indigo-600 border-t-transparent animate-spin" />
          </div>
        ) : filteredLoans.length === 0 ? (
          <div className="text-center py-8">
            <div className="h-12 w-12 mx-auto bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center mb-3">
              <BanknotesIcon className="text-gray-400 h-5 w-5" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
              No Loans Found
            </h3>
            <p className="text-gray-600 dark:text-gray-300 text-xs mb-4">
              {searchTerm || statusFilter !== "all"
                ? "Try adjusting your filters"
                : "No loans have been created yet"}
            </p>
            {canCreateApplication && (
              <button
                onClick={() => setShowLoanApplicationModal(true)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium inline-flex items-center gap-2 px-3 py-2 transition-all duration-200 hover:shadow-md"
              >
                <PlusIcon className="h-3 w-3" />
                New Application
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  {[
                    "Employee",
                    "Loan Type",
                    "Principal Amount",
                    "Outstanding Balance",
                    "Monthly Payment",
                    "Status",
                    "Start Date",
                    "Actions",
                  ].map((col) => (
                    <th key={col}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredLoans.map((loan) => (
                  <tr key={loan.id}>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="h-6 w-6 rounded flex items-center justify-center bg-indigo-50 dark:bg-indigo-900/20">
                          <UsersIcon className="h-3 w-3 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white text-xs">
                            {loan.employee.firstName} {loan.employee.lastName}
                          </p>
                          <p className="text-gray-500 dark:text-gray-400 text-xs">
                            {loan.employee.employeeNumber}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="font-medium text-gray-900 dark:text-white text-xs">
                      {loan.loanType.name}
                    </td>
                    <td className="font-medium text-gray-900 dark:text-white text-xs">
                      {formatCurrency(Number(loan.principalAmount))}
                    </td>
                    <td className="font-medium text-xs text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(Number(loan.outstandingBalance))}
                    </td>
                    <td className="font-medium text-gray-900 dark:text-white text-xs">
                      {formatCurrency(Number(loan.monthlyPayment))}
                    </td>
                    <td>
                      <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${getStatusColor(loan.status)}`}>
                        {loan.status.charAt(0).toUpperCase() + loan.status.slice(1)}
                      </span>
                    </td>
                    <td className="text-gray-600 dark:text-gray-400 text-xs">
                      {loan.startDate ? formatDate(loan.startDate) : 'N/A'}
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        {canRecordPayment && loan.status === 'active' && (
                          <button
                            onClick={() => {
                              setSelectedLoan(loan)
                              setShowPaymentModal(true)
                            }}
                            className="text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                            title="Record Payment"
                          >
                            <CreditCardIcon className="h-3 w-3" />
                          </button>
                        )}
                        {canDeleteLoan && ['pending', 'draft'].includes(loan.status) && (
                          <button
                            onClick={() => handleDeleteLoan(loan)}
                            className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                            title="Delete Loan"
                          >
                            <TrashIcon className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {showLoanApplicationModal && (
        <LoanApplicationModal
          onClose={() => setShowLoanApplicationModal(false)}
          onSuccess={() => {
            refetchApplications()
            refetchLoans()
          }}
        />
      )}

      {showApprovalModal && selectedApplication && (
        <LoanApplicationApprovalModal
          application={selectedApplication}
          onClose={() => {
            setShowApprovalModal(false)
            setSelectedApplication(null)
          }}
          onSuccess={() => {
            refetchApplications()
            refetchLoans()
          }}
        />
      )}

      {showPaymentModal && selectedLoan && (
        <LoanPaymentModal
          loan={selectedLoan}
          onClose={() => {
            setShowPaymentModal(false)
            setSelectedLoan(null)
          }}
          onSuccess={() => {
            refetchLoans()
          }}
        />
      )}
    </div>
  )
}

export default AllLoans